import { digitsToHindi, digitsToLatin } from "@bale/utils";
import jalali from "jalaali-js";
import { strings } from "intl/fa";

export const OneDayInMS = 86400000; // 24 * 60 * 60 * 1000;
export const OneMonth = OneDayInMS * 30;

export const persianMonths = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const intlDateTimeFormat = new Intl.DateTimeFormat("fa-IR");
const intlDateTimeFormatShortComplete = Intl.DateTimeFormat("fa-IR", {
  timeStyle: "medium",
  dateStyle: "short",
});
const intlDateTimeFormatYMD = new Intl.DateTimeFormat("fa-IR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const intlDateTimeFormatHM = new Intl.DateTimeFormat("fa-IR", {
  hour: "numeric",
  minute: "numeric",
});

export const getLocalTime = (timestamp: number) => {
  return intlDateTimeFormat.format(timestamp);
};

export const getLocaleDateTime = (timestamp: number) => {
  return intlDateTimeFormatShortComplete.format(timestamp);
};

export const getLocalTimeHoursMinutes = (timestamp: number): string => {
  return intlDateTimeFormatHM.format(timestamp);
};

export const getCurrentTime = (): string => getLocalTime(Date.now());

export const getJalaliDateObject = (date?: number) => {
  const stringDate = digitsToLatin(
    intlDateTimeFormat.format(date ?? Date.now())
  );
  const dateArray = stringDate.split("/");
  return {
    year: Number(dateArray[0]),
    month: Number(dateArray[1]),
    day: Number(dateArray[2]),
  };
};

// new Date(X) => جمعه
export const dateToWeekDay = (date: Date): string | undefined => {
  const dayNumber = date.getDay();
  return strings.common.weekDays[
    String(dayNumber) as keyof typeof strings.common.weekDays
  ];
};

// valid for 1343 to 1472 in Jalali Calendar
export const isLeapYear = (year: number): boolean =>
  [1, 5, 9, 13, 17, 22, 26, 30].includes(year % 33);

export const getSaturdayDate = (): {
  year: number;
  month: number;
  day: number;
} => {
  const now = new Date();
  const weekDayIndex = now.getDay() === 6 ? 0 : now.getDay() + 1;
  const diff = now.getDate() - weekDayIndex;
  const saturday = new Date(now.setDate(diff));
  const jalaaliSaturday = digitsToLatin(intlDateTimeFormat.format(saturday));
  const jSaturday = jalaaliSaturday.split("/");
  return {
    year: Number(jSaturday[0]),
    month: Number(jSaturday[1]),
    day: Number(jSaturday[2]),
  };
};

export const getMonthDays = (year: number, month: number): number =>
  month < 7 ? 31 : month === 12 && !isLeapYear(year) ? 29 : 30;

export const getLocalDateTimeDialog = (timestamp: number): string => {
  const date = new Date(timestamp);
  if (date.toDateString() === new Date().toDateString()) {
    return intlDateTimeFormatHM.format(date);
  } else if (
    date.toDateString() === new Date(Date.now() - OneDayInMS).toDateString()
  ) {
    return "دیروز";
  } else {
    const { day, month } = getLocalDateObject(date);
    return `${day} ${month}`;
  }
};

export function getTodayDate(): DateType {
  const date = jalali.toJalaali(new Date());
  return {
    year: date.jy,
    month: date.jm,
    day: date.jd,
  };
}

export const getLocalDateObject = (d: Date | number) => {
  const localDateParts = intlDateTimeFormatYMD.formatToParts(d);
  return {
    day: localDateParts.find(({ type }) => type === "day")?.value,
    month: localDateParts.find(({ type }) => type === "month")?.value,
    year: localDateParts.find(({ type }) => type === "year")?.value,
  };
};

export const getLocalDateChat = (
  timestamp: number,
  showYear = false
): string => {
  const date = new Date(timestamp);
  const { day, month, year } = getLocalDateObject(date);
  const thisYear = getLocalDateObject(new Date()).year;
  if (year !== thisYear || showYear) {
    return `${day} ${month} ${year}`;
  }
  return `${day} ${month}`;
};

export const isTodayLastDayOfMonth = (): boolean => {
  const { month, day, year } = getJalaliDateObject();
  const monthDays = getMonthDays(year, month);
  return monthDays === day;
};

export const formatSeconds = (total: number) => {
  const hours = Math.floor(total / (60 * 60));
  const remainingSeconds = total % (60 * 60);
  const minute = Math.floor(remainingSeconds / 60);
  const seconds = Math.floor(remainingSeconds % 60);

  const result: number[] = [];
  if (hours > 0) {
    result.push(hours);
  }
  result.push(minute, seconds);
  return result.map((t) => t.toString().padStart(2, "0")).join(":");
};

export const numberToHour = (number: number | String) => {
  return `${String(number).padStart(2, "0")}:00`;
};

// 12:00 (2)=> 14:00
export const timeWithLeadingZero = (inputTime: number): string => {
  if (inputTime > 24) {
    return String(inputTime - 24).padStart(2, "0") + ":00";
  }
  return String(inputTime).padStart(2, "0") + ":00";
};

// {year: 1400 , month: 1, day: 1} => '1400/01/01'
export const JalaliToRawPersian = (inputDate: DateType): string => {
  return `${inputDate.year}/${String(inputDate.month).padStart(
    2,
    "0"
  )}/${String(inputDate.day).padStart(2, "0")}`;
};

export const SecondsInDay = OneDayInMS / 1000;

export const delay = <T extends any>(ms: number, result?: T) =>
  new Promise<T | undefined>((res) => setTimeout(() => res(result), ms));

export const delayReject = <T extends any>(ms: number) =>
  new Promise<T>((_, rej) => setTimeout(() => rej("Error"), ms));

export function toGregorian(year: number, month: number, day: number): Date {
  const gDate = jalali.toGregorian(year, month, day);
  return new Date(gDate.gy, gDate.gm - 1, gDate.gd);
}

export function jalaliCurrentDate() {
  return jalali.toJalaali(new Date());
}

export function dateObjToJalali(inputTime: Date) {
  return jalali.toJalaali(inputTime);
}

export function getFirstDayOfMonth() {
  const j = jalaliCurrentDate();
  return toGregorian(j.jy, j.jm, 1);
}

export function epochToJalali(timestamp: number) {
  return jalali.toJalaali(new Date(timestamp));
}

export function epochToDateType(timestamp: number): DateType {
  const jl = jalali.toJalaali(new Date(timestamp));
  return {
    year: jl.jy,
    month: jl.jm,
    day: jl.jd,
  };
}
export interface DateType {
  year: number;
  month: number;
  day: number;
}

export function jalaliToEpoch(date: DateType, tillNight = false): number {
  const gregorianTime = toGregorian(date.year, date.month, date.day);
  if (tillNight)
    return new Date(gregorianTime.setHours(23, 59, 59, 999)).getTime();

  // we will set the time to noon by default to prevent timezone problems
  return new Date(gregorianTime.setHours(12, 0, 0, 0)).getTime();
}

// 6 => 21600000
export function hourToEpoch(inputHour: number): number {
  return inputHour * 60 * 60 * 1000;
}

// 17:55 => 64500000
export function epochToHour(inputHour: number): string {
  const dt = new Date(inputHour);
  const hr = dt.getHours();
  const m = "0" + dt.getMinutes();
  return hr + ":" + m.substr(-2);
}

export function epochToFullDateTime(inputEpouch: number): string {
  return (
    epochToHour(inputEpouch) +
    " - " +
    JalaliToRawPersian(epochToDateType(inputEpouch))
  );
}

export function isFirstDateGraterThanSecondDate(
  date1: DateType,
  date2: DateType
): boolean {
  return jalaliToEpoch(date1) > jalaliToEpoch(date2);
}

export function isBeforeToday(date: DateType): boolean {
  return jalaliToEpoch(date) < jalaliToEpoch(getJalaliDateObject());
}

export function isAfterToday(date: DateType): boolean {
  return jalaliToEpoch(date) > jalaliToEpoch(getJalaliDateObject());
}

// ISO Time: '2023-08-12T08:40:15.724Z'
export function isISOTimeAfterToday(date: string): boolean {
  const DateObj = new Date(date).getTime();
  return DateObj > Date.now();
}

export const isDateToday = (date: DateType): boolean => {
  const today = jalaliCurrentDate();
  return (
    today.jd === date.day && today.jm === date.month && today.jy === date.year
  );
};

export const isDateFirstDayOfMonth = (date: DateType): boolean => {
  const today = jalaliCurrentDate();
  return date.day === 1 && today.jm === date.month && today.jy === date.year;
};

export const isDateFor30DaysAgo = (date: DateType): boolean => {
  const theDate = getJalaliDateObject(Date.now() - 29 * OneDayInMS);
  return (
    date.day === theDate.day &&
    date.month === theDate.month &&
    date.year === theDate.year
  );
};

export const isToday = (time: number) => {
  const theDay = getJalaliDateObject(time);
  const today = getJalaliDateObject();
  return (
    theDay.day === today.day &&
    theDay.month === today.month &&
    theDay.year === today.year
  );
};

export const formatEpoch = (time: number) => {
  return getLocaleDateTime(time).replace("،", " - ").slice(0, -3);
};

export const compareDateType = (
  firstDateType: DateType,
  secondDateType: DateType
) => {
  if (firstDateType.year > secondDateType.year) {
    return 1;
  } else if (firstDateType.year < secondDateType.year) {
    return -1;
  }
  if (firstDateType.month > secondDateType.month) {
    return 1;
  } else if (firstDateType.month < secondDateType.month) {
    return -1;
  }
  if (firstDateType.day > secondDateType.day) {
    return 1;
  } else if (firstDateType.day < secondDateType.day) {
    return -1;
  }
  return 0;
};

export const rawPersianToEpoch = (text: string): number =>
  jalaliToEpoch({
    year: Number(digitsToLatin(text.split("/")[0])),
    month: Number(digitsToLatin(text.split("/")[1])),
    day: Number(digitsToLatin(text.split("/")[2])),
  });

export const convertToIsoDate = (isoDate: string) => {
  return new Date(isoDate).toISOString();
};

// 1691315787873 => ۱۵ مرداد ۱۴۰۲
export const getLocaleDateTimeWithMonth = (timestamp: number) => {
  return intlDateTimeFormatYMD.format(timestamp);
};

export const getEpochDistanceInDays = (date: string | undefined) => {
  if (!date) return 0;
  const now = Date.now();
  const epoch = new Date(date).getTime();
  return Math.floor((epoch - now) / OneDayInMS);
};

export const dateTimeWithDash = (date: string) => {
  const dateObj = new Date(date);
  const time = dateObj.getMinutes() + " : " + dateObj.getHours();
  return (
    digitsToHindi(time) +
    " - " +
    digitsToHindi(JalaliToRawPersian(getJalaliDateObject(dateObj.getTime())))
  );
};

export function formatMonthAndDayAndHour(timestamp: number): string {
  return `${digitsToHindi(getLocalDateObject(timestamp).day)} ${digitsToHindi(
    getLocalDateObject(timestamp).month
  )} - ${digitsToHindi(getLocalTimeHoursMinutes(timestamp))}`;
}

export function addHourToDate(timestamp: number, hour: number): number {
  return timestamp + hour * 60 * 60 * 1000;
}
